import { Injectable } from "@angular/core";
import { AlteredShapeConfiguration } from "~/src/utils/shape";
import { ColorCodes } from "~/src/utils/color-constants";
import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import * as MergeBuffer from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { DimensionNormalShape, DimensionShapeSelector } from "~/src/utils/shape-facetype";
import {
    BufferGeometry,
    EdgesGeometry,
    Vector3,
    BoxGeometry,
    Float32BufferAttribute,
    CylinderGeometry,
    SphereGeometry,
    CircleGeometry,
    Mesh,
    MeshBasicMaterial,
    ColorRepresentation,
    DoubleSide,
    LineSegments,
} from "three";

@Injectable({
    providedIn: "root",
})
export class ShapeResourceManagerService {
    private geometries: {
        [key in MeshType]: BufferGeometry;
    } = {} as { [key in MeshType]: BufferGeometry };

    private outlineGeometries: {
        [key in MeshType]: EdgesGeometry;
    } = {} as { [key in MeshType]: EdgesGeometry };

    private geometriesForSelector: {
        [key in MeshType]: BufferGeometry;
    } = {} as { [key in MeshType]: BufferGeometry };

    private outlineGeometriesForSelector: {
        [key in MeshType]: EdgesGeometry;
    } = {} as { [key in MeshType]: EdgesGeometry };

    private offsetCoordinatesForSelector: {
        [key in MeshType]: Vector3 | null;
    } = {} as { [key in MeshType]: Vector3 | null };

    constructor() {}

    public initializeResources(isForSelector: boolean): void {
        // Initialize geometries and their outlines in one pass
        Object.entries(MeshType).forEach(([key, type]) => {
            let geometry: BufferGeometry;
            let offSetCoordinatesForSelector: Vector3 = new Vector3();
            let height;
            let radius;
            let vertices = [];
            let materialIndices: number[] = [];
            let triangleOffset: number;
            let angleStep;
            let scaleFactor;
            let segments;
            let baseVertices: Vector3[] = [];
            let offset;
            let frontOffset;

            switch (type) {
                case MeshType.Cube:
                    geometry = new BoxGeometry(
                        isForSelector ? DimensionShapeSelector.CubeWidth : DimensionNormalShape.CubeWidth,
                        isForSelector ? DimensionShapeSelector.CubeHeight : DimensionNormalShape.CubeHeight,
                        isForSelector ? DimensionShapeSelector.CubeDepth : DimensionNormalShape.CubeDepth,
                    );

                    // Add material groups (automatically assigned by BoxGeometry)
                    geometry.groups.forEach((group, index) => {
                        group.materialIndex = index; // Assign material index
                    });

                    offSetCoordinatesForSelector.set(-8, 5, 0);
                    break;
                // case MeshType.Cuboid:
                //     geometry = new BoxGeometry(20, 28.28, 20);
                //     break;
                case MeshType.CurvedCube:
                    geometry = new BufferGeometry();

                    height = isForSelector
                        ? DimensionShapeSelector.CurvedCubeHeight
                        : DimensionNormalShape.CurvedCubeHeight;
                    segments = isForSelector
                        ? DimensionShapeSelector.CurvedCubeSegements
                        : DimensionNormalShape.CurvedCubeSegements;
                    const halfHeight = height / 2;

                    vertices = [];
                    materialIndices = [];

                    // Generate curved surface
                    for (let i = 0; i < segments; i++) {
                        const angle1 = (Math.PI / 2) * (i / segments);
                        const angle2 = (Math.PI / 2) * ((i + 1) / segments);

                        const x1 = Math.cos(angle1) * height - halfHeight;
                        const y1 = Math.sin(angle1) * height - halfHeight;
                        const x2 = Math.cos(angle2) * height - halfHeight;
                        const y2 = Math.sin(angle2) * height - halfHeight;

                        vertices.push(
                            x1,
                            y1,
                            halfHeight,
                            x1,
                            y1,
                            -halfHeight,
                            x2,
                            y2,
                            -halfHeight,

                            x1,
                            y1,
                            halfHeight,
                            x2,
                            y2,
                            -halfHeight,
                            x2,
                            y2,
                            halfHeight,
                        );

                        materialIndices.push(0, 0); // Two triangles per segment
                    }

                    // Create left face
                    for (let i = 0; i < segments; i++) {
                        const angle = (Math.PI / 2) * (i / segments);
                        const angleNext = (Math.PI / 2) * ((i + 1) / segments);

                        vertices.push(
                            -halfHeight,
                            -halfHeight,
                            halfHeight,
                            Math.cos(angle) * height - halfHeight,
                            Math.sin(angle) * height - halfHeight,
                            halfHeight,
                            Math.cos(angleNext) * height - halfHeight,
                            Math.sin(angleNext) * height - halfHeight,
                            halfHeight,
                        );

                        materialIndices.push(3);
                    }

                    // Create right face
                    for (let i = 0; i < segments; i++) {
                        const angle = (Math.PI / 2) * (i / segments);
                        const angleNext = (Math.PI / 2) * ((i + 1) / segments);

                        vertices.push(
                            -halfHeight,
                            -halfHeight,
                            -halfHeight,
                            Math.cos(angleNext) * height - halfHeight,
                            Math.sin(angleNext) * height - halfHeight,
                            -halfHeight,
                            Math.cos(angle) * height - halfHeight,
                            Math.sin(angle) * height - halfHeight,
                            -halfHeight,
                        );

                        materialIndices.push(4);
                    }

                    // Create bottom face
                    vertices.push(
                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        -halfHeight,
                        -halfHeight,

                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        halfHeight,
                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        -halfHeight,
                        halfHeight,
                    );

                    materialIndices.push(2, 2);

                    // Create back face
                    vertices.push(
                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        halfHeight,
                        -halfHeight,
                        halfHeight,
                        -halfHeight,

                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        -halfHeight,
                        halfHeight,
                        -halfHeight,
                        halfHeight,
                        halfHeight,
                    );

                    materialIndices.push(1, 1);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Set material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    // Compute normals
                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(-3, 5, 0);
                    break;

                case MeshType.CurvedCube2:
                    geometry = new BufferGeometry();

                    // Define scale factors based on isForSelector
                    radius = isForSelector
                        ? DimensionShapeSelector.CurvedCube2Radius
                        : DimensionNormalShape.CurvedCube2Radius; // Scale radius
                    height = isForSelector
                        ? DimensionShapeSelector.CurvedCube2Height
                        : DimensionNormalShape.CurvedCube2Height; // Scale height

                    segments = isForSelector
                        ? DimensionShapeSelector.CurvedCube2Segements
                        : DimensionNormalShape.CurvedCube2Segements;

                    // Store the points of the curve for later use with side faces
                    const curvePointsLeft = [];
                    const curvePointsRight = [];

                    // Create the curved top surface
                    for (let i = 0; i < segments; i++) {
                        const theta1 = (i / segments) * Math.PI;
                        const theta2 = ((i + 1) / segments) * Math.PI;

                        const x1 = radius * Math.cos(theta1);
                        const y1 = radius * Math.sin(theta1);
                        const x2 = radius * Math.cos(theta2);
                        const y2 = radius * Math.sin(theta2);

                        // Store points for side faces
                        curvePointsLeft.push({ y: y1, z: -height + (x1 + height) });
                        curvePointsRight.push({ y: y1, z: -height + (x1 + height) });

                        // First triangle of the segment
                        vertices.push(
                            -height,
                            y1,
                            -height + (x1 + height),
                            height,
                            y1,
                            -height + (x1 + height),
                            height,
                            y2,
                            -height + (x2 + height),
                        );

                        // Second triangle of the segment
                        vertices.push(
                            -height,
                            y1,
                            -height + (x1 + height),
                            height,
                            y2,
                            -height + (x2 + height),
                            -height,
                            y2,
                            -height + (x2 + height),
                        );

                        materialIndices.push(2, 2);
                    }

                    // Add side faces for the curved top
                    // Front side
                    for (let i = 0; i < curvePointsRight.length - 1; i++) {
                        const point1 = curvePointsRight[i];
                        const point2 = curvePointsRight[i + 1];

                        // Create triangle
                        vertices.push(height, point1.y, point1.z, height, 0, -height, height, point2.y, point2.z);
                        materialIndices.push(0);
                    }

                    // Back side
                    for (let i = 0; i < curvePointsLeft.length - 1; i++) {
                        const point1 = curvePointsLeft[i];
                        const point2 = curvePointsLeft[i + 1];

                        // Create triangle
                        vertices.push(-height, point1.y, point1.z, -height, point2.y, point2.z, -height, 0, -height);
                        materialIndices.push(1);
                    }

                    // Front face
                    vertices.push(
                        height,
                        0,
                        height,
                        height,
                        -height,
                        height,
                        height,
                        -height,
                        -height,

                        height,
                        0,
                        height,
                        height,
                        -height,
                        -height,
                        height,
                        0,
                        -height,
                    );
                    materialIndices.push(0, 0);

                    // Back face
                    vertices.push(
                        -height,
                        0,
                        -height,
                        -height,
                        -height,
                        -height,
                        -height,
                        -height,
                        height,

                        -height,
                        0,
                        -height,
                        -height,
                        -height,
                        height,
                        -height,
                        0,
                        height,
                    );
                    materialIndices.push(1, 1);

                    // Bottom face
                    vertices.push(
                        -height,
                        -height,
                        -height,
                        height,
                        -height,
                        -height,
                        height,
                        -height,
                        height,

                        -height,
                        -height,
                        -height,
                        height,
                        -height,
                        height,
                        -height,
                        -height,
                        height,
                    );
                    materialIndices.push(3, 3);

                    // Right face
                    vertices.push(
                        height,
                        0,
                        -height,
                        height,
                        -height,
                        -height,
                        -height,
                        -height,
                        -height,

                        height,
                        0,
                        -height,
                        -height,
                        -height,
                        -height,
                        -height,
                        0,
                        -height,
                    );
                    materialIndices.push(2, 2);

                    // Left face
                    vertices.push(
                        height,
                        0,
                        height,
                        -height,
                        -height,
                        height,
                        height,
                        -height,
                        height,

                        height,
                        0,
                        height,
                        -height,
                        0,
                        height,
                        -height,
                        -height,
                        height,
                    );
                    materialIndices.push(2, 2);

                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    triangleOffset = 0;
                    materialIndices.forEach((materialIndex) => {
                        geometry.addGroup(triangleOffset, 3, materialIndex);
                        triangleOffset += 3;
                    });

                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(3, 5, 0);
                    break;
                case MeshType.HalfCylinder:
                    geometry = new BufferGeometry();
                    radius = isForSelector
                        ? DimensionShapeSelector.HalfCylinderRadius
                        : DimensionNormalShape.HalfCylinderRadius;
                    const length = isForSelector
                        ? DimensionShapeSelector.HalfCylinderLength
                        : DimensionNormalShape.HalfCylinderLength;
                    segments = isForSelector
                        ? DimensionShapeSelector.HalfCylinderSegments
                        : DimensionNormalShape.HalfCylinderSegments; // Controls smoothness of the curved surface
                    angleStep = Math.PI / segments; // Half cylinder covers 180 degrees

                    // Generate curved surface faces (material index 0)
                    for (let i = 0; i < segments; i++) {
                        const angle1 = i * angleStep;
                        const angle2 = (i + 1) * angleStep;

                        const x1 = radius * Math.cos(angle1);
                        const y1 = radius * Math.sin(angle1);
                        const x2 = radius * Math.cos(angle2);
                        const y2 = radius * Math.sin(angle2);

                        // Left vertices
                        const v1 = new Vector3(x1, y1, length / 2);
                        const v2 = new Vector3(x2, y2, length / 2);

                        // Right vertices
                        const v3 = new Vector3(x1, y1, -length / 2);
                        const v4 = new Vector3(x2, y2, -length / 2);

                        vertices.push(v3.x, v3.y, v3.z, v2.x, v2.y, v2.z, v1.x, v1.y, v1.z);

                        vertices.push(v3.x, v3.y, v3.z, v4.x, v4.y, v4.z, v2.x, v2.y, v2.z);
                        materialIndices.push(0, 0);
                    }

                    // Generate left face (material index 1)
                    const leftCenter = new Vector3(0, 0, length / 2);
                    for (let i = 0; i < segments; i++) {
                        const angle1 = i * angleStep;
                        const angle2 = (i + 1) * angleStep;

                        const x1 = radius * Math.cos(angle1);
                        const y1 = radius * Math.sin(angle1);
                        const x2 = radius * Math.cos(angle2);
                        const y2 = radius * Math.sin(angle2);

                        const v1 = new Vector3(x1, y1, length / 2);
                        const v2 = new Vector3(x2, y2, length / 2);

                        vertices.push(leftCenter.x, leftCenter.y, leftCenter.z, v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
                        materialIndices.push(2);
                    }

                    // Generate right face (material index 2)
                    const backCenter = new Vector3(0, 0, -length / 2);
                    for (let i = 0; i < segments; i++) {
                        const angle1 = i * angleStep;
                        const angle2 = (i + 1) * angleStep;

                        const x1 = radius * Math.cos(angle1);
                        const y1 = radius * Math.sin(angle1);
                        const x2 = radius * Math.cos(angle2);
                        const y2 = radius * Math.sin(angle2);

                        const v1 = new Vector3(x1, y1, -length / 2);
                        const v2 = new Vector3(x2, y2, -length / 2);

                        vertices.push(v2.x, v2.y, v2.z, v1.x, v1.y, v1.z, backCenter.x, backCenter.y, backCenter.z);
                        materialIndices.push(3);
                    }

                    // Generate bottom rectangular face (material index 3)
                    const backLeft = new Vector3(-radius, 0, -length / 2);
                    const backRight = new Vector3(-radius, 0, length / 2);
                    const frontLeft = new Vector3(radius, 0, -length / 2);
                    const frontRight = new Vector3(radius, 0, length / 2);

                    // First triangle (frontLeft → frontRight → backRight) - Corrected order
                    vertices.push(
                        frontLeft.x,
                        frontLeft.y,
                        frontLeft.z,
                        frontRight.x,
                        frontRight.y,
                        frontRight.z,
                        backRight.x,
                        backRight.y,
                        backRight.z,
                    );
                    materialIndices.push(1);

                    // Second triangle (frontLeft → backRight → backLeft) - Corrected order
                    vertices.push(
                        frontLeft.x,
                        frontLeft.y,
                        frontLeft.z,
                        backRight.x,
                        backRight.y,
                        backRight.z,
                        backLeft.x,
                        backLeft.y,
                        backLeft.z,
                    );
                    materialIndices.push(1);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Define material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    // Compute normals for lighting
                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(8, 5, 0);
                    break;
                case MeshType.Prism:
                    geometry = new BufferGeometry();
                    scaleFactor = isForSelector
                        ? DimensionShapeSelector.PrismScaleFactor
                        : DimensionNormalShape.PrismScaleFactor;
                    // Left face (triangular)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        0,
                        scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(3);

                    // Right face (triangular)
                    vertices.push(
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        0,
                        scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(4);

                    // Bottom face (rectangular)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,

                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(2, 2);

                    // Front face (rectangular)
                    vertices.push(
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        0,
                        scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,

                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        0,
                        scaleFactor,
                        -scaleFactor,
                        0,
                        scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(0, 0);

                    // Back face (rectangular)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        0,
                        scaleFactor,
                        scaleFactor,

                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        0,
                        scaleFactor,
                        scaleFactor,
                        0,
                        scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(1, 1);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Set material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(-8, 0, 0);
                    break;
                case MeshType.SquarePyramid:
                    geometry = new BufferGeometry();
                    scaleFactor = isForSelector
                        ? DimensionShapeSelector.SquarePyramidScaleFactor
                        : DimensionNormalShape.SquarePyramidScaleFactor;
                    // Left face (triangular)
                    vertices.push(
                        0,
                        scaleFactor,
                        0,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(3);

                    // Right face (triangular)
                    vertices.push(
                        0,
                        scaleFactor,
                        0,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(4);

                    // Bottom face (square)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,

                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(2, 2);

                    // Front face (triangular)
                    vertices.push(
                        0,
                        scaleFactor,
                        0,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(0);

                    // Back face (triangular)
                    vertices.push(
                        0,
                        scaleFactor,
                        0,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(1);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Set material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(-3, 0, 0);
                    break;
                case MeshType.Cone:
                    geometry = new BufferGeometry();
                    radius = isForSelector ? DimensionShapeSelector.PrismRadius : DimensionNormalShape.PrismRadius;
                    height = isForSelector ? DimensionShapeSelector.PrismHeight : DimensionNormalShape.PrismHeight;
                    segments = isForSelector
                        ? DimensionShapeSelector.PrismSegements
                        : DimensionNormalShape.PrismSegements;
                    angleStep = (2 * Math.PI) / segments;

                    // Apex of the cone
                    const apex = new Vector3(0, height / 2, 0);

                    // Base center
                    const baseCenter = new Vector3(0, -height / 2, 0);

                    // Generate base vertices]
                    for (let i = 0; i <= segments; i++) {
                        const angle = i * angleStep;
                        const x = radius * Math.cos(angle);
                        const z = radius * Math.sin(angle);
                        baseVertices.push(new Vector3(x, baseCenter.y, z));
                    }

                    // Create side faces (material index 0)
                    for (let i = 0; i < segments; i++) {
                        const current = baseVertices[i];
                        const next = baseVertices[i + 1];

                        vertices.push(
                            apex.x,
                            apex.y,
                            apex.z, // Apex
                            next.x,
                            next.y,
                            next.z, // Next base vertex
                            current.x,
                            current.y,
                            current.z, // Current base vertex
                        );

                        materialIndices.push(0);
                    }

                    // Create base faces (material index 1)
                    for (let i = 0; i < segments; i++) {
                        const current = baseVertices[i];
                        const next = baseVertices[i + 1];

                        vertices.push(
                            baseCenter.x,
                            baseCenter.y,
                            baseCenter.z, // Base center
                            current.x,
                            current.y,
                            current.z, // Current base vertex
                            next.x,
                            next.y,
                            next.z, // Next base vertex
                        );

                        materialIndices.push(1);
                    }

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Define material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    // Compute normals for lighting
                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(3, 0, 0);
                    break;
                case MeshType.Cylinder:
                    geometry = new CylinderGeometry(
                        isForSelector
                            ? DimensionShapeSelector.CylinderRadiusTop
                            : DimensionNormalShape.CylinderRadiusTop,
                        isForSelector
                            ? DimensionShapeSelector.CylinderRadiusBottom
                            : DimensionNormalShape.CylinderRadiusBottom,
                        isForSelector ? DimensionShapeSelector.CylinderHeight : DimensionNormalShape.CylinderHeight,
                        isForSelector ? DimensionShapeSelector.CylinderSegments : DimensionNormalShape.CylinderSegments,
                    );

                    // Add material groups (automatically assigned by BoxGeometry)
                    geometry.groups.forEach((group, index) => {
                        group.materialIndex = index; // Assign material index
                    });

                    offSetCoordinatesForSelector.set(8, 0, 0);
                    break;
                case MeshType.HemiSphere:
                    radius = isForSelector
                        ? DimensionShapeSelector.HemiSphereRadius
                        : DimensionNormalShape.HemiSphereRadius;
                    const widthSegments = isForSelector
                        ? DimensionShapeSelector.HemisphereWidthSegments
                        : DimensionNormalShape.HemisphereWidthSegments;
                    const heightSegments = isForSelector
                        ? DimensionShapeSelector.HemisphereHeightSegments
                        : DimensionNormalShape.HemisphereHeightSegments;

                    // Create hemisphere geometry
                    const hemisphereGeometry = new SphereGeometry(
                        radius,
                        widthSegments,
                        heightSegments,
                        0,
                        Math.PI * 2,
                        0,
                        Math.PI / 2, // Half-sphere
                    );

                    const baseGeometry = new CircleGeometry(radius, widthSegments);
                    baseGeometry.rotateX(Math.PI / 2);

                    geometry = (MergeBuffer as any).mergeGeometries([hemisphereGeometry, baseGeometry]);

                    const groupStartHemisphere = 0;
                    const groupCountHemisphere = hemisphereGeometry.index!.count;
                    geometry.addGroup(groupStartHemisphere, groupCountHemisphere, 0); // Assign material index 0 to the hemisphere

                    const groupStartBase = groupCountHemisphere;
                    const groupCountBase = baseGeometry.index!.count;
                    geometry.addGroup(groupStartBase, groupCountBase, 1); // Assign material index 1 to the base

                    offSetCoordinatesForSelector.set(-8, -5, 0);
                    break;
                case MeshType.RightTriangle:
                    geometry = new BufferGeometry();

                    scaleFactor = isForSelector
                        ? DimensionShapeSelector.RightTriangleScaleFactor
                        : DimensionNormalShape.RightTriangleScaleFactor;

                    // Left face (triangular)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                    );

                    materialIndices.push(3);

                    // Right face (triangular)
                    vertices.push(
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(4);

                    // Bottom face (square)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,

                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                    );
                    materialIndices.push(2, 2);

                    // Front face (rectangular)
                    vertices.push(
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,

                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(0, 0);

                    // Back face (square)
                    vertices.push(
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,

                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                        scaleFactor,
                        -scaleFactor,
                    );
                    materialIndices.push(1, 1);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Set material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(-3, -5, 0);
                    break;
                    // case MeshType.ConcavePrism:
                    geometry = new BufferGeometry();

                    vertices = [];
                    materialIndices = [];

                    // Calculate offset to center the mesh
                    const depth = isForSelector
                        ? DimensionShapeSelector.ConcavePrismDepth
                        : DimensionNormalShape.ConcavePrismDepth;
                    const halfDepth = depth / 2;
                    offset = -halfDepth;
                    segments = isForSelector
                        ? DimensionShapeSelector.ConcavePrismSegmenets
                        : DimensionNormalShape.ConcavePrismSegmenets;

                    // Create curved surface (material index 0)
                    for (let i = 0; i < segments; i++) {
                        const angle1 = (Math.PI / 2) * (i / segments);
                        const angle2 = (Math.PI / 2) * ((i + 1) / segments);

                        // Front curve points
                        const frontX1 = -depth * Math.cos(angle1) - offset;
                        const frontY1 = -depth * Math.sin(angle1) - offset;
                        const frontX2 = -depth * Math.cos(angle2) - offset;
                        const frontY2 = -depth * Math.sin(angle2) - offset;

                        // Back curve points (same x,y but different z)
                        const backX1 = frontX1;
                        const backY1 = frontY1;
                        const backX2 = frontX2;
                        const backY2 = frontY2;

                        // First triangle
                        vertices.push(
                            backX1,
                            backY1,
                            halfDepth,
                            frontX2,
                            frontY2,
                            -halfDepth,
                            frontX1,
                            frontY1,
                            -halfDepth,
                        );

                        // Second triangle
                        vertices.push(
                            backX1,
                            backY1,
                            halfDepth,
                            backX2,
                            backY2,
                            halfDepth,
                            frontX2,
                            frontY2,
                            -halfDepth,
                        );

                        materialIndices.push(0, 0); // Two triangles per segment
                    }

                    // Create left face (material index 1)
                    for (let i = 0; i < segments; i++) {
                        const angle1 = (Math.PI / 2) * (i / segments);
                        const angle2 = (Math.PI / 2) * ((i + 1) / segments);

                        const x1 = -depth * Math.cos(angle1) - offset;
                        const y1 = -depth * Math.sin(angle1) - offset;
                        const x2 = -depth * Math.cos(angle2) - offset;
                        const y2 = -depth * Math.sin(angle2) - offset;

                        vertices.push(-halfDepth, -halfDepth, halfDepth, x2, y2, halfDepth, x1, y1, halfDepth);

                        materialIndices.push(3);
                    }

                    // Create right face (material index 2)
                    for (let i = 0; i < segments; i++) {
                        const angle1 = (Math.PI / 2) * (i / segments);
                        const angle2 = (Math.PI / 2) * ((i + 1) / segments);

                        const x1 = -depth * Math.cos(angle1) - offset;
                        const y1 = -depth * Math.sin(angle1) - offset;
                        const x2 = -depth * Math.cos(angle2) - offset;
                        const y2 = -depth * Math.sin(angle2) - offset;

                        vertices.push(-halfDepth, -halfDepth, -halfDepth, x1, y1, -halfDepth, x2, y2, -halfDepth);

                        materialIndices.push(4);
                    }

                    // Create back face (material index 3)
                    vertices.push(
                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,
                        halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,

                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,
                        halfDepth,
                        halfDepth,
                    );

                    materialIndices.push(1, 1);

                    // Create bottom face (material index 4)
                    vertices.push(
                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,

                        -halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,
                        -halfDepth,
                        halfDepth,
                        -halfDepth,
                        halfDepth,
                    );

                    materialIndices.push(2, 2);

                    // Set geometry attributes
                    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

                    // Set material groups
                    triangleOffset = 0;
                    for (let i = 0; i < materialIndices.length; i++) {
                        geometry.addGroup(triangleOffset, 3, materialIndices[i]);
                        triangleOffset += 3;
                    }

                    // Compute normals
                    geometry.computeVertexNormals();

                    offSetCoordinatesForSelector.set(3, -5, 0);
                    break;
                default:
                    geometry = new BoxGeometry(0, 0, 0);
            }

            geometry.name = `${key}-Geometry`;

            if (type === MeshType.HalfCylinder || type === MeshType.HemiSphere) {
                isForSelector ? geometry.translate(0, -1.5, 0) : geometry.translate(0, -10, 0);
            } else {
                geometry.center();
            }

            // Compute the bounding box
            geometry.computeBoundingBox();

            if (isForSelector) {
                this.geometriesForSelector[type] = geometry;
                this.offsetCoordinatesForSelector[type] = offSetCoordinatesForSelector;
            } else {
                this.geometries[type] = geometry;
            }
        });
    }

    public addOutlineToMesh(mesh: Mesh, isForSelector: boolean): void {
        const outlineMaterial = new MeshBasicMaterial({
            color: ColorCodes.black as ColorRepresentation,
            side: DoubleSide,
        });

        if (isForSelector) {
            let outlineGeometry = this.outlineGeometriesForSelector[mesh.name as MeshType];
            if (!outlineGeometry) {
                outlineGeometry = new EdgesGeometry(mesh.geometry, AlteredShapeConfiguration.standardThreshHoldAngle);
                this.outlineGeometriesForSelector[mesh.name as MeshType] = outlineGeometry;
            }
            const outline = new LineSegments(outlineGeometry, outlineMaterial);
            outline.name = AuxiliaryObjectType.Outline;
            mesh.add(outline);
        } else {
            let outlineGeometry = this.outlineGeometries[mesh.name as MeshType];
            if (!outlineGeometry) {
                outlineGeometry = new EdgesGeometry(mesh.geometry, AlteredShapeConfiguration.standardThreshHoldAngle);
                this.outlineGeometries[mesh.name as MeshType] = outlineGeometry;
            }
            const outline = new LineSegments(outlineGeometry, outlineMaterial);
            outline.name = AuxiliaryObjectType.Outline;
            mesh.add(outline);
        }
    }

    public getGeometry(type: MeshType, isForSelector: boolean): BufferGeometry {
        return isForSelector ? this.geometriesForSelector[type] : this.geometries[type];
    }

    public getOutlineGeometry(type: MeshType, isForSelector: boolean): EdgesGeometry {
        return isForSelector ? this.outlineGeometriesForSelector[type] : this.outlineGeometries[type];
    }

    public getOffsetCoordinatesForSelector(type: MeshType) {
        return this.offsetCoordinatesForSelector[type];
    }
}
